import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { IDS } from '@/constants/testIds/wizard';
import { Play, Loader2 } from 'lucide-react';

const Field = ({ label, children, hint }) => (
  <div className="flex flex-col gap-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
    {hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
  </div>
);

const NumInput = ({ value, onChange, step = 0.1, min, max, testid }) => (
  <Input
    type="number"
    value={value ?? 0}
    step={step}
    min={min}
    max={max}
    onChange={e => onChange(parseFloat(e.target.value))}
    className="h-8 text-sm tabular bg-black/30"
    data-testid={testid}
  />
);

const IntInput = ({ value, onChange, min = 0, max, testid }) => (
  <Input
    type="number"
    value={value ?? 0}
    step={1}
    min={min}
    max={max}
    onChange={e => onChange(parseInt(e.target.value || '0', 10))}
    className="h-8 text-sm tabular bg-black/30"
    data-testid={testid}
  />
);

const set = (params, setParams, key, val) => setParams({ ...params, [key]: val });

const PocketForm = ({ p, sp }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Forma">
      <Select value={p.shape} onValueChange={v => sp({ ...p, shape: v })}>
        <SelectTrigger className="h-8" data-testid="param-pocket-shape"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="rect">Teglalap</SelectItem>
          <SelectItem value="circle">Kor</SelectItem>
        </SelectContent>
      </Select>
    </Field>
    <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
    {p.shape === 'rect' ? <>
      <Field label="Szelesseg X (mm)"><NumInput value={p.width} onChange={v => set(p, sp, 'width', v)} /></Field>
      <Field label="Magassag Y (mm)"><NumInput value={p.height} onChange={v => set(p, sp, 'height', v)} /></Field>
    </> : <>
      <Field label="Atmero (mm)"><NumInput value={p.diameter} onChange={v => set(p, sp, 'diameter', v)} /></Field>
      <Field label=" "><span /></Field>
    </>}
    <Field label="Lepcsozes Z (mm)"><NumInput value={p.step_down} onChange={v => set(p, sp, 'step_down', v)} /></Field>
    <Field label="Oldallepes (%)"><NumInput value={p.stepover_pct} onChange={v => set(p, sp, 'stepover_pct', v)} min={5} max={95} step={1} /></Field>
    <Field label="Szerszam atmero (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
    <Field label="Szerszam sorszam (T)"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
    <Field label="Fordulatszam (RPM)"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
    <Field label="Eltolasi seb. XY (mm/min)"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
    <Field label="Beszurasi seb. Z (mm/min)"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    <Field label="Origo X"><NumInput value={p.origin_x} onChange={v => set(p, sp, 'origin_x', v)} /></Field>
    <Field label="Origo Y"><NumInput value={p.origin_y} onChange={v => set(p, sp, 'origin_y', v)} /></Field>
  </div>
);

const ContourForm = ({ p, sp }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
      <Field label="Lepcsozes Z (mm)"><NumInput value={p.step_down} onChange={v => set(p, sp, 'step_down', v)} /></Field>
      <Field label="Szerszam atmero (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
      <Field label="Szerszam sorszam"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
      <Field label="Oldal">
        <Select value={p.side} onValueChange={v => sp({ ...p, side: v })}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="on">Pont pontosan (“on”)</SelectItem>
            <SelectItem value="left">Bal oldal</SelectItem>
            <SelectItem value="right">Jobb oldal</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Zart kontur">
        <div className="flex items-center gap-2 h-8">
          <Switch checked={p.closed} onCheckedChange={v => sp({ ...p, closed: v })} />
          <span className="text-xs">{p.closed ? 'Igen' : 'Nem'}</span>
        </div>
      </Field>
      <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
      <Field label="Feed XY (mm/min)"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
      <Field label="Feed Z (mm/min)"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    </div>
    <div>
      <Label className="text-xs text-muted-foreground">Pontok (X,Y soronkent)</Label>
      <textarea
        className="w-full h-32 mt-1 rounded-md bg-black/40 border border-border p-2 text-xs mono tabular"
        value={(p.points || []).map(pt => `${pt[0]}, ${pt[1]}`).join('\n')}
        onChange={e => {
          const arr = e.target.value.split('\n').map(l => l.split(',').map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n))).filter(a => a.length === 2);
          sp({ ...p, points: arr });
        }}
        data-testid="param-contour-points"
      />
      <span className="text-[10px] text-muted-foreground">{(p.points || []).length} pont. DXF import elerheto a felso menuben.</span>
    </div>
  </div>
);

const DrillingForm = ({ p, sp }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
      <Field label="Peck lepes (mm)"><NumInput value={p.peck} onChange={v => set(p, sp, 'peck', v)} /></Field>
      <Field label="Stilus">
        <Select value={p.style} onValueChange={v => sp({ ...p, style: v })}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="peck">Peck (szakaszos)</SelectItem>
            <SelectItem value="simple">Egyszeru</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Dwell (mp)"><NumInput value={p.dwell} onChange={v => set(p, sp, 'dwell', v)} step={0.1} /></Field>
      <Field label="Retract Z (mm)"><NumInput value={p.retract_z} onChange={v => set(p, sp, 'retract_z', v)} /></Field>
      <Field label="Furoszal atmero (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
      <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
      <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
      <Field label="Feed Z (mm/min)"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    </div>
    <div>
      <Label className="text-xs text-muted-foreground">Furatok (X,Y soronkent)</Label>
      <textarea
        className="w-full h-28 mt-1 rounded-md bg-black/40 border border-border p-2 text-xs mono tabular"
        value={(p.holes || []).map(h => `${h[0]}, ${h[1]}`).join('\n')}
        onChange={e => {
          const arr = e.target.value.split('\n').map(l => l.split(',').map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n))).filter(a => a.length === 2);
          sp({ ...p, holes: arr });
        }}
      />
      <span className="text-[10px] text-muted-foreground">{(p.holes || []).length} furat</span>
    </div>
  </div>
);

const FacingForm = ({ p, sp }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Szelesseg X (mm)"><NumInput value={p.width} onChange={v => set(p, sp, 'width', v)} /></Field>
    <Field label="Magassag Y (mm)"><NumInput value={p.height} onChange={v => set(p, sp, 'height', v)} /></Field>
    <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
    <Field label="Lepcsozes Z (mm)"><NumInput value={p.step_down} onChange={v => set(p, sp, 'step_down', v)} /></Field>
    <Field label="Homlokmaro atmero (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
    <Field label="Oldallepes (%)"><NumInput value={p.stepover_pct} onChange={v => set(p, sp, 'stepover_pct', v)} step={5} /></Field>
    <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
    <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
    <Field label="Feed XY (mm/min)"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
    <Field label="Feed Z (mm/min)"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    <Field label="Origo X"><NumInput value={p.origin_x} onChange={v => set(p, sp, 'origin_x', v)} /></Field>
    <Field label="Origo Y"><NumInput value={p.origin_y} onChange={v => set(p, sp, 'origin_y', v)} /></Field>
  </div>
);

const EngravingForm = ({ p, sp }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} step={0.05} /></Field>
      <Field label="Szerszam atm. (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} step={0.1} /></Field>
      <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
      <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
      <Field label="Feed XY"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
      <Field label="Feed Z"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    </div>
    <div>
      <Label className="text-xs text-muted-foreground">Polilines (uj sor = uj polyline, X,Y soronkent). Ures sor: uj polyline.</Label>
      <textarea
        className="w-full h-40 mt-1 rounded-md bg-black/40 border border-border p-2 text-xs mono tabular"
        value={(p.polylines || []).map(poly => poly.map(pt => `${pt[0]}, ${pt[1]}`).join('\n')).join('\n\n')}
        onChange={e => {
          const groups = e.target.value.split(/\n\s*\n/);
          const polys = groups.map(g => g.split('\n').map(l => l.split(',').map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n))).filter(a => a.length === 2)).filter(a => a.length >= 2);
          sp({ ...p, polylines: polys });
        }}
      />
      <span className="text-[10px] text-muted-foreground">{(p.polylines || []).length} polyline</span>
    </div>
  </div>
);

const ThreadForm = ({ p, sp }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Nevleges atm. (mm)"><NumInput value={p.nominal_diameter} onChange={v => set(p, sp, 'nominal_diameter', v)} /></Field>
    <Field label="Menetemelkedes P (mm)"><NumInput value={p.pitch} onChange={v => set(p, sp, 'pitch', v)} step={0.1} /></Field>
    <Field label="Menet hossz (mm)"><NumInput value={p.length} onChange={v => set(p, sp, 'length', v)} /></Field>
    <Field label="Szerszam atm. (mm)"><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
    <Field label="Tipus">
      <Select value={p.thread_type} onValueChange={v => sp({ ...p, thread_type: v })}>
        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="internal">Belso</SelectItem>
          <SelectItem value="external">Kulso</SelectItem>
        </SelectContent>
      </Select>
    </Field>
    <Field label="Irany">
      <Select value={p.direction} onValueChange={v => sp({ ...p, direction: v })}>
        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="right">Jobbmenet</SelectItem>
          <SelectItem value="left">Balmenet</SelectItem>
        </SelectContent>
      </Select>
    </Field>
    <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
    <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
    <Field label="Feed XY"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
    <Field label="Feed Z"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
    <Field label="Kozeppont X"><NumInput value={p.center_x} onChange={v => set(p, sp, 'center_x', v)} /></Field>
    <Field label="Kozeppont Y"><NumInput value={p.center_y} onChange={v => set(p, sp, 'center_y', v)} /></Field>
    <Field label="Z felszin"><NumInput value={p.z_top} onChange={v => set(p, sp, 'z_top', v)} /></Field>
  </div>
);

const ArcForm = ({ p, sp }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Kozeppont X"><NumInput value={p.center_x} onChange={v => set(p, sp, 'center_x', v)} /></Field>
    <Field label="Kozeppont Y"><NumInput value={p.center_y} onChange={v => set(p, sp, 'center_y', v)} /></Field>
    <Field label="Sugar (mm)"><NumInput value={p.radius} onChange={v => set(p, sp, 'radius', v)} /></Field>
    <Field label="Kezdo szog (deg)"><NumInput value={p.start_angle_deg} onChange={v => set(p, sp, 'start_angle_deg', v)} step={1} /></Field>
    <Field label="Ivszög (deg)"><NumInput value={p.sweep_deg} onChange={v => set(p, sp, 'sweep_deg', v)} step={1} /></Field>
    <Field label="Irany">
      <Select value={p.direction} onValueChange={v => sp({ ...p, direction: v })}>
        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ccw">Ora ellen (CCW)</SelectItem>
          <SelectItem value="cw">Ora fele (CW)</SelectItem>
        </SelectContent>
      </Select>
    </Field>
    <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
    <Field label="Lepcsozes Z"><NumInput value={p.step_down} onChange={v => set(p, sp, 'step_down', v)} /></Field>
    <Field label="Szerszam atm."><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
    <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
    <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
    <Field label="Feed XY"><NumInput value={p.feed_xy} onChange={v => set(p, sp, 'feed_xy', v)} step={10} /></Field>
    <Field label="Feed Z"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
  </div>
);

const GridForm = ({ p, sp }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Sorok"><IntInput value={p.rows} onChange={v => set(p, sp, 'rows', v)} min={1} max={100} /></Field>
    <Field label="Oszlopok"><IntInput value={p.cols} onChange={v => set(p, sp, 'cols', v)} min={1} max={100} /></Field>
    <Field label="dX (mm)"><NumInput value={p.dx} onChange={v => set(p, sp, 'dx', v)} /></Field>
    <Field label="dY (mm)"><NumInput value={p.dy} onChange={v => set(p, sp, 'dy', v)} /></Field>
    <Field label="Origo X"><NumInput value={p.origin_x} onChange={v => set(p, sp, 'origin_x', v)} /></Field>
    <Field label="Origo Y"><NumInput value={p.origin_y} onChange={v => set(p, sp, 'origin_y', v)} /></Field>
    <Field label="Melyseg (mm)"><NumInput value={p.depth} onChange={v => set(p, sp, 'depth', v)} /></Field>
    <Field label="Peck (mm)"><NumInput value={p.peck} onChange={v => set(p, sp, 'peck', v)} /></Field>
    <Field label="Stilus">
      <Select value={p.style} onValueChange={v => sp({ ...p, style: v })}>
        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="peck">Peck</SelectItem>
          <SelectItem value="simple">Egyszeru</SelectItem>
        </SelectContent>
      </Select>
    </Field>
    <Field label="Furoszal atm."><NumInput value={p.tool_diameter} onChange={v => set(p, sp, 'tool_diameter', v)} /></Field>
    <Field label="T"><IntInput value={p.tool_num} onChange={v => set(p, sp, 'tool_num', v)} min={1} max={99} /></Field>
    <Field label="RPM"><IntInput value={p.rpm} onChange={v => set(p, sp, 'rpm', v)} min={100} max={60000} /></Field>
    <Field label="Feed Z"><NumInput value={p.feed_z} onChange={v => set(p, sp, 'feed_z', v)} step={10} /></Field>
  </div>
);

export const ParameterPanel = ({ op, params, setParams, onGenerate, busy }) => {
  const FORMS = {
    pocket: PocketForm, contour: ContourForm, drilling: DrillingForm,
    facing: FacingForm, engraving: EngravingForm, thread: ThreadForm,
    arc: ArcForm, grid: GridForm,
  };
  const Form = FORMS[op] || PocketForm;
  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid={IDS.left.parameterForm}>
      <div className="panel-header sticky top-0">
        <span className="panel-title">Parameterek</span>
        <span className="text-xs text-muted-foreground uppercase">{op}</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        <Form p={params} sp={setParams} />
      </div>
      <div className="p-3 border-t border-border/70">
        <Button
          onClick={onGenerate}
          disabled={busy}
          className="w-full bg-primary text-primary-foreground hover:opacity-90 font-semibold"
          data-testid={IDS.left.generate}
        >
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generalas...</> : <><Play className="h-4 w-4 mr-2" /> G-kod generalasa</>}
        </Button>
      </div>
    </div>
  );
};

export default ParameterPanel;
